import { useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import ConsultationForm from "./ConsultationForm";

/**
 * The Fleet Preservation Assessment request: the multi-step consultation form
 * with the interest preset to the fleet program, so the visitor lands straight
 * on the fleet-size question. `children` is the trigger element (rendered via
 * DialogTrigger asChild), so each entry point keeps its own button styling.
 *
 * Accepts optional controlled `open`/`onOpenChange` for triggers that need to
 * drive the dialog themselves — e.g. a real <a> trigger, whose click must
 * preventDefault (Radix's DialogTrigger never prevents navigation itself, and
 * its composed handler skips its own toggle once default is prevented).
 * Uncontrolled by default, so <button> triggers keep working untouched.
 */
export default function AssessmentDialog({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* dvh + inner scroll keeps the form usable on short mobile viewports */}
      <DialogContent className="max-w-2xl max-h-[90dvh] grid-rows-[auto_1fr] gap-0 p-0 overflow-hidden">
        <DialogHeader className="border-b border-border px-5 pt-5 pb-4 pr-12 text-left">
          <DialogTitle>Request Your Fleet Preservation Assessment</DialogTitle>
          <DialogDescription>
            A few quick questions about your fleet and we'll get back to you to scope your assessment — no cost, no obligation.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto overscroll-contain p-3 sm:p-5">
          <ConsultationForm
            presetInterest="Protect My Fleet"
            formName="Fleet Preservation Assessment"
            submitLabel="Request Fleet Preservation Assessment"
            successNote="a specialist will reach out within 1–2 business days to scope your fleet preservation assessment."
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
